import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

/**
 * Order bump that adds or removes the Shipping Protection line item from the cart.
 *
 * The protection is a real (unlisted) product, so the only thing this element does is
 * mutate the cart the same way any add-to-cart does: dispatch a `CartLinesUpdateEvent`
 * carrying a deferred promise, then resolve it with the re-rendered cart sections so
 * every `cart-items-component` on the page morphs itself.
 *
 * Two invariants hold, because the cart can also be changed from places this element
 * doesn't control (a second tab, the hidden line's own quantity selector, a stale page
 * restored from bfcache):
 *
 * - the protection never sits in the cart on its own
 * - its quantity is never above 1
 *
 * Every decision is made against a freshly read cart rather than against the rendered
 * attributes. The rendered cart goes stale constantly, because the drawer markup is cached,
 * bfcache restores whole pages, and a second tab can empty the cart underneath this
 * one, and acting on a stale render produces exactly the failures the invariants are
 * meant to prevent: removing a line key that no longer exists (a silent no-op the
 * shopper reads as a dead checkbox), or adding the protection to a cart that turns out
 * to be empty, only for the orphan rule to strip it right back out.
 *
 * @extends {Component}
 */
class ShippingProtectionComponent extends Component {
  /** Guards against overlapping mutations while a request is in flight. */
  #busy = false;

  // A cart morph re-renders this element in place as often as it replaces it, so the
  // invariants have to be re-checked on attribute changes too, not just on connect.
  static observedAttributes = ['data-orphan', 'data-line-key', 'data-quantity'];

  connectedCallback() {
    super.connectedCallback();
    this.#enforceInvariants();
  }

  attributeChangedCallback() {
    if (this.isConnected) this.#enforceInvariants();
  }

  /** Reads the cart as it actually is, rather than as this page last rendered it. */
  async #readCart() {
    const cart = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => response.json());

    const variantId = this.dataset.variantId;
    const line = cart.items?.find((item) => String(item.variant_id) === variantId);

    return {
      cart,
      /** The protection's live cart line, or undefined when it isn't in the cart. */
      line,
      /** Whether the cart holds anything the protection could actually protect. */
      hasOtherItems: Boolean(cart.items?.some((item) => String(item.variant_id) !== variantId)),
    };
  }

  async #enforceInvariants() {
    if (this.#busy) return;

    // Only pay for a cart read when the render claims something is off. A render that
    // already looks consistent is left alone, as it is by far the common case.
    const suspect = this.hasAttribute('data-orphan') || Number(this.dataset.quantity) > 1;
    if (!suspect) return;

    const { line, hasOtherItems } = await this.#readCart();
    if (!line || this.#busy) return;

    if (!hasOtherItems) {
      // Nothing left to protect, so drop the stranded line so the cart empties out.
      this.#mutate({ action: 'remove', body: { id: line.key, quantity: 0 } });
      return;
    }

    if (line.quantity > 1) {
      this.#mutate({ action: 'update', body: { id: line.key, quantity: 1 } });
    }
  }

  /**
   * Adds or removes the protection in response to the checkbox.
   * @param {Event} event - The change event from the checkbox.
   */
  toggle = async (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    if (this.#busy) {
      // Snap the checkbox back; the in-flight request owns the state.
      input.checked = !input.checked;
      return;
    }

    const variantId = this.dataset.variantId;
    if (!variantId) return;

    // Claim the element before awaiting, so a second click can't start a second
    // mutation while this one is still reading the cart.
    this.#busy = true;
    this.classList.add('shipping-protection--busy');

    let state;
    try {
      state = await this.#readCart();
    } catch (error) {
      this.#release();
      input.checked = !input.checked;
      this.dispatchEvent(
        new CartErrorEvent({ error: error?.message || 'Could not read the cart', code: 'SERVICE_UNAVAILABLE' })
      );
      return;
    }

    const { line, hasOtherItems } = state;
    this.#release();

    if (input.checked) {
      // Adding to a cart with nothing else in it would immediately trip the orphan
      // rule, which reads to the shopper as a checkbox that does nothing. The render
      // is out of date; show them the cart they really have.
      if (!hasOtherItems) return this.#resync();
      if (line) return this.#resync();

      this.#mutate({
        action: 'add',
        url: Theme.routes.cart_add_url,
        body: { items: [{ id: Number(variantId), quantity: 1 }] },
        lines: [{ merchandiseId: variantId, quantity: 1 }],
      });
    } else {
      // Remove by the key the cart reports now. The rendered one may point at a line
      // that has since been replaced or dropped, and removing it would be a no-op.
      if (!line) return this.#resync();

      this.#mutate({
        action: 'remove',
        body: { id: line.key, quantity: 0 },
        lines: [{ id: line.key, quantity: 0 }],
      });
    }
  };

  #release() {
    this.#busy = false;
    this.classList.remove('shipping-protection--busy');
  }

  /**
   * Re-renders the cart from the server. Used when this page's idea of the cart turns
   * out to be wrong: no mutation is wanted, the shopper just needs to see the truth.
   */
  #resync() {
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: deferred.promise })
    );

    // Resolving without `sections` makes every cart-items-component fetch its own
    // section fresh, which is exactly the repaint needed here.
    fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((response) => response.json())
      .then((cart) => {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { source: 'shipping-protection-component', items: cart.items, didError: false },
        });
      })
      .catch((error) => deferred.reject(error));
  }

  /**
   * Posts a cart mutation and hands the re-rendered sections to the cart components.
   *
   * @param {object} config
   * @param {'add' | 'remove' | 'update'} config.action - Action reported on the event.
   * @param {string} [config.url] - Cart endpoint. Defaults to the change endpoint.
   * @param {object} config.body - JSON body for the endpoint.
   * @param {Array<object>} [config.lines] - Lines reported on the event.
   */
  #mutate({ action, url, body, lines }) {
    this.#busy = true;
    this.classList.add('shipping-protection--busy');


    const sections = new Set();
    for (const component of document.querySelectorAll('cart-items-component')) {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sections.add(component.dataset.sectionId);
      }
    }

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action,
        context: 'cart',
        lines: lines ?? [],
        promise: deferred.promise,
      })
    );

    fetch(
      url ?? Theme.routes.cart_change_url,
      fetchConfig('json', {
        body: JSON.stringify({
          ...body,
          sections: Array.from(sections).join(','),
          sections_url: window.location.pathname,
        }),
      })
    )
      .then((response) => response.json())
      .then(async (data) => {
        if (data.status) {
          throw new Error(data.description || data.message || 'Shipping protection update failed');
        }

        // `/cart/add.js` answers with the added item rather than the cart, so read
        // the cart back for the event payload. `data.sections` is the same in both
        // responses, and is all the cart components need in order to morph.
        const cart = await fetch(`${Theme.routes.cart_url}.js`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        }).then((response) => response.json());

        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            source: 'shipping-protection-component',
            items: cart.items,
            sections: data.sections,
            didError: false,
          },
        });
      })
      .catch((error) => {
        deferred.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Shipping protection update failed',
            code: 'SERVICE_UNAVAILABLE',
          })
        );

        // Re-render from the cart rather than guessing the checkbox back: the mutation
        // may have half-applied, and this element's own attributes are what just proved
        // untrustworthy.
        this.#release();
        this.#resync();
      })
      .finally(() => {
        this.#release();
      });
  }
}

if (!customElements.get('shipping-protection-component')) {
  customElements.define('shipping-protection-component', ShippingProtectionComponent);
}
